import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LogOut, MapPin, Clock, Calendar, ChevronRight, CheckCircle, PlayCircle, ArrowLeft, GraduationCap, Trophy, Shield, Menu, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MatchControl from '@/components/turno/MatchControl';
import StaffAcademiasDashboard from './StaffAcademiasDashboard';
import ConsultaIdentidad from '@/components/ConsultaIdentidad';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TurnoDashboard({ user }) {
  const [modulo, setModulo] = useState(null);
  const [fechas, setFechas] = useState([]);
  const [clasesAcademia, setClasesAcademia] = useState([]);
  const [selectedFecha, setSelectedFecha] = useState(null);
  const [selectedPartido, setSelectedPartido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('fechas');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { loadDatos(); }, []);

  const loadDatos = async () => {
    try {
      const [fechasRes, clasesRes] = await Promise.all([
        axios.get(`${API}/turno/mis-fechas`, { withCredentials: true }).catch(() => ({ data: { fechas: [] } })),
        axios.get(`${API}/staff/mis-clases`, { withCredentials: true }).catch(() => ({ data: { clases: [] } }))
      ]);
      setFechas(fechasRes.data.fechas || []);
      setClasesAcademia(clasesRes.data.clases || []);
      if (fechasRes.data.fechas.length > 0 && clasesRes.data.clases.length === 0) {
        setModulo('ligas');
      } else if (fechasRes.data.fechas.length === 0 && clasesRes.data.clases.length > 0) {
        setModulo('academias');
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadFechaDetail = async (canchaId) => {
    try {
      const response = await axios.get(`${API}/turno/fecha/${canchaId}`, { withCredentials: true });
      setSelectedFecha(response.data);
    } catch (error) {
      toast.error('Error al cargar detalle de fecha');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      toast.success('Sesion cerrada');
      window.location.href = '/ligas';
    } catch (error) {
      toast.error('Error al cerrar sesion');
    }
  };

  const handleSelectFecha = (fecha) => {
    loadFechaDetail(fecha.cancha_id);
    setActiveSection('fecha-detail');
  };

  const handleSelectPartido = (partido, primerPendienteId) => {
    if (partido.estado === 'en_progreso' || partido.id === primerPendienteId) {
      setSelectedPartido(partido);
    } else {
      toast.error('Solo puedes trabajar en el partido mas antiguo pendiente');
    }
  };

  const handleBackToFechaList = () => {
    setSelectedFecha(null);
    setSelectedPartido(null);
    setActiveSection('fechas');
    loadDatos();
  };

  const handleBackToPartidoList = () => {
    setSelectedPartido(null);
    loadFechaDetail(selectedFecha.cancha.id);
  };

  const handleSetSection = (section) => {
    if (section === 'fechas') {
      setSelectedFecha(null);
      setSelectedPartido(null);
    }
    setActiveSection(section);
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  if (modulo === 'academias') {
    return <StaffAcademiasDashboard user={user} />;
  }

  // Module selector when user has both
  if (!modulo && fechas.length > 0 && clasesAcademia.length > 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-4xl w-full">
          <h2 className="text-2xl font-bold text-center text-white mb-8">Selecciona tu area</h2>
          <div className="grid grid-cols-2 gap-6">
            <Card
              className="p-8 cursor-pointer hover:shadow-xl transition-shadow text-center bg-slate-800 border-slate-700 hover:bg-slate-750"
              onClick={() => setModulo('ligas')}
            >
              <Trophy className="w-16 h-16 text-white mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-white">Ligas</h3>
              <p className="text-slate-400 mb-4">{fechas.length} fechas asignadas</p>
              <Button className="w-full bg-white text-slate-900 hover:bg-slate-200">Ir a Ligas</Button>
            </Card>
            <Card
              className="p-8 cursor-pointer hover:shadow-xl transition-shadow text-center bg-slate-800 border-slate-700 hover:bg-slate-750"
              onClick={() => setModulo('academias')}
            >
              <GraduationCap className="w-16 h-16 text-white mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-white">Academias</h3>
              <p className="text-slate-400 mb-4">{clasesAcademia.length} clases asignadas</p>
              <Button className="w-full bg-white text-slate-900 hover:bg-slate-200">Ir a Academias</Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Match control view
  if (selectedPartido) {
    return <MatchControl partido={selectedPartido} onBack={handleBackToPartidoList} />;
  }

  const NAV_ITEMS = [
    { id: 'fechas', label: 'Mis Fechas', icon: Calendar },
    { id: 'identidad', label: 'Identidad', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="turno-dashboard">
      {/* Top Bar */}
      <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            data-testid="turno-sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img src="/kinosis-logo.png" alt="Kinosis" className="h-8 sm:h-10 w-auto" />
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-slate-900 font-bold text-sm">
              {user.name?.charAt(0) || user.email?.charAt(0)}
            </div>
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-white">{user.name || user.email}</p>
              <p className="text-xs text-slate-400">Turno</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-700" data-testid="turno-logout-btn">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Cerrar Sesion</span>
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 bg-black/50 z-20" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:relative inset-y-0 left-0 z-30
          w-64 bg-slate-800 border-r border-slate-700 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:transform-none lg:shrink-0
          pt-16 lg:pt-0
        `}>
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center">
                <Trophy className="w-6 h-6 text-slate-900" />
              </div>
              <div>
                <h2 className="font-bold text-white">Panel de Turno</h2>
                <p className="text-xs text-slate-400">Control de partidos</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const isActive = activeSection === item.id || (item.id === 'fechas' && activeSection === 'fecha-detail');
              return (
                <button
                  key={item.id}
                  onClick={() => handleSetSection(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive ? 'bg-white text-slate-900' : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                  data-testid={`turno-nav-${item.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {activeSection === 'identidad' && (
            <ConsultaIdentidad mode="turno" />
          )}

          {activeSection === 'fechas' && (
            <>
              <h2 className="text-2xl font-bold text-white mb-6">Mis Fechas</h2>
              {fechas.length === 0 ? (
                <Card className="p-12 text-center bg-slate-800 border-slate-700">
                  <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Sin Fechas Asignadas</h3>
                  <p className="text-slate-400">No tienes fechas activas asignadas</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {fechas.map((fecha) => (
                    <Card
                      key={fecha.fecha_id}
                      className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-slate-800 border-slate-700 hover:bg-slate-750"
                      onClick={() => handleSelectFecha(fecha)}
                      data-testid={`fecha-card-${fecha.fecha_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Calendar className="w-5 h-5 text-white" />
                            <h3 className="text-lg font-bold text-white">{fecha.fecha_descripcion}</h3>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                            <MapPin className="w-4 h-4" />
                            <span>{fecha.cancha_nombre}</span>
                            <span className="mx-2">-</span>
                            <Clock className="w-4 h-4" />
                            <span>{fecha.fecha_fecha}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl font-bold text-white">{fecha.total_partidos}</span>
                              <span className="text-sm text-slate-400">Partidos</span>
                            </div>
                            {fecha.en_progreso > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                                <span className="text-sm font-semibold text-emerald-400">{fecha.en_progreso} en vivo</span>
                              </div>
                            )}
                            <div className="text-sm text-slate-400">
                              {fecha.finalizados}/{fecha.total_partidos} finalizados
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-6 h-6 text-slate-500" />
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {activeSection === 'fecha-detail' && selectedFecha && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <Button
                  onClick={handleBackToFechaList}
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-slate-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver
                </Button>
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedFecha.cancha.nombre_cancha}</h2>
                  <p className="text-sm text-slate-400">{selectedFecha.fecha.descripcion}</p>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white mb-4">Partidos</h3>
              <div className="space-y-4">
                {selectedFecha.partidos?.map((partido) => {
                  const puedeEditar = partido.estado === 'en_progreso' || partido.id === selectedFecha.primer_pendiente_id;
                  return (
                    <Card
                      key={partido.id}
                      className={`p-4 transition-shadow bg-slate-800 border-slate-700 ${puedeEditar ? 'hover:shadow-md cursor-pointer' : 'opacity-60'}`}
                      data-testid={`partido-card-${partido.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="flex items-center gap-2 text-slate-400">
                              <Clock className="w-4 h-4" />
                              <span className="font-semibold">{partido.hora}</span>
                            </div>
                            <span
                              className={`px-2 py-1 text-xs rounded font-semibold ${
                                partido.estado === 'finalizado'
                                  ? 'bg-slate-700 text-slate-300'
                                  : partido.estado === 'en_progreso'
                                  ? 'bg-emerald-900/50 text-emerald-400'
                                  : 'bg-blue-900/50 text-blue-400'
                              }`}
                            >
                              {partido.estado === 'finalizado' ? 'Finalizado' : partido.estado === 'en_progreso' ? 'En Progreso' : 'Pendiente'}
                            </span>
                          </div>
                          <div className="text-lg font-bold text-white">
                            {partido.rival1_nombre_corto}
                            <span className="mx-3 text-slate-500">vs</span>
                            {partido.rival2_nombre_corto}
                          </div>
                          {partido.resultado && (
                            <div className="mt-2 text-2xl font-bold text-emerald-400">
                              {partido.resultado.goles_local} - {partido.resultado.goles_visita}
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={() => handleSelectPartido(partido, selectedFecha.primer_pendiente_id)}
                          disabled={!puedeEditar}
                          className="gap-2 bg-white text-slate-900 hover:bg-slate-200 disabled:opacity-30"
                        >
                          {partido.estado === 'en_progreso' ? (
                            <><PlayCircle className="w-4 h-4" /> Gestionar</>
                          ) : partido.estado === 'finalizado' ? (
                            <><CheckCircle className="w-4 h-4" /> Ver</>
                          ) : (
                            <><PlayCircle className="w-4 h-4" /> Iniciar</>
                          )}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
